// Uploaded profile photos are cropped to a centred square and resized in the browser, so what the
// server stores (a data URL on the account) stays small. Matches the server's limit in
// validations/profileValidation.js.
export const PHOTO_MAX_LENGTH = 90000;
const PHOTO_SIZE = 256;
const MAX_FILE_BYTES = 15 * 1024 * 1024;
const QUALITIES = [0.86, 0.75, 0.62, 0.5];

// FileReader (a data: URL) rather than URL.createObjectURL: the CSP allows data: images, not blob:.
const readAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(new Error("That file couldn't be read."));
    reader.readAsDataURL(file);
  });

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("That file isn't an image this browser can open."));
    image.src = src;
  });

/** A 256 × 256 JPEG data URL of the image's centred square, or an Error to show. */
export const preparePhoto = async (file: File): Promise<string> => {
  if (!file.type.startsWith("image/")) {
    throw new Error("Choose an image file.");
  }
  if (file.size > MAX_FILE_BYTES) {
    throw new Error("That image is over 15 MB. Choose a smaller one.");
  }
  const image = await loadImage(await readAsDataUrl(file));
  const side = Math.min(image.naturalWidth, image.naturalHeight);
  if (!side) {
    throw new Error("That image is empty.");
  }
  const canvas = document.createElement("canvas");
  canvas.width = PHOTO_SIZE;
  canvas.height = PHOTO_SIZE;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("This browser can't resize images.");
  }
  // JPEG has no transparency; a transparent PNG gets a white background instead of black.
  context.fillStyle = "white";
  context.fillRect(0, 0, PHOTO_SIZE, PHOTO_SIZE);
  context.drawImage(
    image,
    (image.naturalWidth - side) / 2,
    (image.naturalHeight - side) / 2,
    side,
    side,
    0,
    0,
    PHOTO_SIZE,
    PHOTO_SIZE
  );
  for (const quality of QUALITIES) {
    const dataUrl = canvas.toDataURL("image/jpeg", quality);
    if (dataUrl.length <= PHOTO_MAX_LENGTH) {
      return dataUrl;
    }
  }
  throw new Error("That image has too much detail to use. Try another one.");
};
